import { useState, useEffect } from 'react';
import type { Rule, PunishmentStep } from '../types';
import { useCreateRule, useUpdateRule } from '../hooks/useRules';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../api/errorHandler';
import PunishmentStepEditor from './PunishmentStepEditor';

interface RuleModalProps {
  guildId: string | null;
  rule: Rule | null;
  onClose: () => void;
}

export default function RuleModal({ guildId, rule, onClose }: RuleModalProps) {
  const { showToast } = useToast();
  const [name, setName] = useState(rule?.name || '');
  const [content, setContent] = useState(rule?.content || '');
  const [punishments, setPunishments] = useState<PunishmentStep[]>(
    rule?.punishments || []
  );

  const createRule = useCreateRule(guildId);
  const updateRule = useUpdateRule(guildId);

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setContent(rule.content);
      setPunishments(rule.punishments);
    }
  }, [rule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !content.trim()) {
      showToast('error', '규칙 이름과 내용을 입력해주세요');
      return;
    }

    if (punishments.length === 0) {
      showToast('error', '최소 1개의 제재 단계를 추가해주세요');
      return;
    }

    try {
      if (rule) {
        await updateRule.mutateAsync({
          ruleName: rule.name,
          content,
          punishments,
        });
        showToast('success', '규칙이 수정되었습니다');
      } else {
        await createRule.mutateAsync({
          name,
          content,
          punishments,
        });
        showToast('success', '규칙이 생성되었습니다');
      }
      onClose();
    } catch (error) {
      showToast('error', getErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={onClose}
          aria-hidden="true"
        />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="mb-4">
                <h3 id="modal-title" className="text-lg font-medium text-gray-900 mb-4">
                  {rule ? '규칙 수정' : '새 규칙 추가'}
                </h3>

                <div className="mb-4">
                  <label htmlFor="rule-name" className="block text-sm font-medium text-gray-700 mb-2">
                    규칙 이름
                  </label>
                  <input
                    id="rule-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!!rule}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="예: 욕설 금지"
                    aria-required="true"
                    aria-describedby={rule ? "rule-name-help" : undefined}
                  />
                  {rule && (
                    <p id="rule-name-help" className="mt-1 text-xs text-gray-500">
                      규칙 이름은 수정할 수 없습니다
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label htmlFor="rule-content" className="block text-sm font-medium text-gray-700 mb-2">
                    규칙 내용
                  </label>
                  <textarea
                    id="rule-content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="규칙에 대한 설명을 입력하세요"
                    aria-required="true"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제재 단계
                  </label>
                  <PunishmentStepEditor
                    punishments={punishments}
                    onChange={setPunishments}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={createRule.isPending || updateRule.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] text-base font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={createRule.isPending || updateRule.isPending ? 'Saving...' : 'Save rule'}
              >
                {createRule.isPending || updateRule.isPending ? '저장 중...' : '저장'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                aria-label="Cancel"
              >
                취소
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
